import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme, VisualizerBackgroundMode, VisualizerMode } from '../../types';
import {
  DEFAULT_LATENT_BACKGROUND_TUNING,
  DEFAULT_MONET_BACKGROUND_TUNING,
  DEFAULT_NOMAND_BACKGROUND_TUNING,
} from '../../types';
import {
  getVisualizerModeLabel,
  VISUALIZER_REGISTRY,
} from '../../components/visualizer/registry';
import {
  getVisualizerBackgroundModeLabel,
  getVisualizerBackgroundRegistryEntry,
  VISUALIZER_BACKGROUND_REGISTRY,
} from '../../components/visualizer/backgrounds/registry';
import type {
  VisualizerBackgroundActions,
  VisualizerBackgroundConfig,
} from '../../components/visualizer/backgrounds/definition';
import { BackgroundModeGlyph, VisualizerModeGlyph } from '../../components/visualizer/modeGlyphs';
import { QuickControlChip } from '../../components/shared/QuickControlChip';
import ModeStepperRow from '../../components/panelTab/controls/ModeStepperRow';
import type LyricStageSettingsPanel from '../LyricStageSettingsPanel';

// packages/lyric-stage/src/host/chrome/LyricStageAppearanceControls.tsx
// Folia AppearanceSection 的独立版：歌词样式 / 背景两行步进器 + 可选详细参数。

const ANIMATION_INTENSITIES: Theme['animationIntensity'][] = ['calm', 'normal', 'chaotic'];

export interface LyricStageAppearanceControlsProps {
  theme: Theme;
  onThemeChange?: (theme: Theme) => void;
  isDaylight?: boolean;
  onDaylightChange?: (isDaylight: boolean) => void;
  visualizerMode: VisualizerMode;
  onVisualizerModeChange: (mode: VisualizerMode) => void;
  backgroundConfig: VisualizerBackgroundConfig;
  backgroundActions: VisualizerBackgroundActions;
  showDetailSettings?: boolean;
  onToggleDetailSettings?: () => void;
  detailSettingsOpen?: boolean;
  settingsPanelExtra?: React.ComponentProps<typeof LyricStageSettingsPanel>;
  visualizerBadges?: Record<string, string>;
  visualizerQuickControl?: React.ReactNode;
  backgroundRowOverride?: React.ReactNode;
  hideDaylightControls?: boolean;
}

const LyricStageAppearanceControls: React.FC<LyricStageAppearanceControlsProps> = ({
  theme,
  onThemeChange,
  isDaylight = false,
  onDaylightChange,
  visualizerMode,
  onVisualizerModeChange,
  backgroundConfig,
  backgroundActions,
  visualizerBadges,
  visualizerQuickControl,
  backgroundRowOverride,
  hideDaylightControls = false,
}) => {
  const { t } = useTranslation();

  const visualizerOptions = useMemo(
    () => [...VISUALIZER_REGISTRY].sort((a, b) => a.order - b.order).map(entry => ({
      value: entry.mode,
      label: getVisualizerModeLabel(entry.mode, t),
      badge: visualizerBadges?.[entry.mode],
    })),
    [t, visualizerBadges],
  );

  const backgroundOptions = useMemo(
    () => VISUALIZER_BACKGROUND_REGISTRY.map(entry => ({
      value: entry.mode,
      label: getVisualizerBackgroundModeLabel(entry.mode, t),
    })),
    [t],
  );

  const resolvedBackgroundMode = (backgroundConfig.mode
    ?? 'latent') as VisualizerBackgroundMode;

  const stepVisualizerMode = (direction: -1 | 1) => {
    if (visualizerOptions.length < 2) return;
    const index = visualizerOptions.findIndex(option => option.value === visualizerMode);
    const next = (index < 0 ? 0 : index) + direction;
    const wrapped = ((next % visualizerOptions.length) + visualizerOptions.length) % visualizerOptions.length;
    onVisualizerModeChange(visualizerOptions[wrapped]!.value);
  };

  const stepBackgroundMode = (direction: -1 | 1) => {
    if (backgroundOptions.length < 2) return;
    const index = backgroundOptions.findIndex(option => option.value === resolvedBackgroundMode);
    const next = (index < 0 ? 0 : index) + direction;
    const wrapped = ((next % backgroundOptions.length) + backgroundOptions.length) % backgroundOptions.length;
    backgroundActions.onModeChange?.(backgroundOptions[wrapped]!.value);
  };

  const cycleAnimationIntensity = () => {
    if (!onThemeChange) return;
    const currentIndex = ANIMATION_INTENSITIES.indexOf(theme.animationIntensity);
    onThemeChange({
      ...theme,
      animationIntensity: ANIMATION_INTENSITIES[(currentIndex + 1) % ANIMATION_INTENSITIES.length]!,
    });
  };

  const backgroundQuickControls = getVisualizerBackgroundRegistryEntry(resolvedBackgroundMode).renderQuickControls?.({
    config: {
      common: backgroundConfig.common,
      monet: { tuning: backgroundConfig.monet?.tuning ?? DEFAULT_MONET_BACKGROUND_TUNING },
      nomand: { tuning: backgroundConfig.nomand?.tuning ?? DEFAULT_NOMAND_BACKGROUND_TUNING },
      latent: { tuning: backgroundConfig.latent?.tuning ?? DEFAULT_LATENT_BACKGROUND_TUNING },
    },
    actions: backgroundActions,
    t,
    isDaylight,
    theme,
  });

  return (
    <div className="space-y-1">
      <ModeStepperRow<VisualizerMode>
        value={visualizerMode}
        options={visualizerOptions}
        onSelect={onVisualizerModeChange}
        onStep={stepVisualizerMode}
        renderGlyph={mode => <VisualizerModeGlyph mode={mode} />}
        ariaLabel={t('ui.animationMode')}
        // moreLabel={t('ui.moreSettings')}
        // onOpenMore={() => onToggleDetailSettings?.()}
        isDaylight={isDaylight}
        primaryColor={theme.primaryColor}
        trailing={visualizerQuickControl ?? (
          <QuickControlChip
            isDaylight={isDaylight}
            label={t(`animation.${theme.animationIntensity}`)}
            title={`${t('ui.animationIntensity')}: ${t(`animation.${theme.animationIntensity}`)}`}
            onClick={cycleAnimationIntensity}
          />
        )}
      />

      {backgroundRowOverride ?? <ModeStepperRow<VisualizerBackgroundMode>
        value={resolvedBackgroundMode}
        options={backgroundOptions}
        onSelect={mode => backgroundActions.onModeChange?.(mode)}
        onStep={stepBackgroundMode}
        renderGlyph={mode => <BackgroundModeGlyph mode={mode} />}
        ariaLabel={t('options.visualizerBackgroundMode')}
        // moreLabel={t('ui.moreSettings')}
        // onOpenMore={() => onToggleDetailSettings?.()}
        isDaylight={isDaylight}
        primaryColor={theme.primaryColor}
        trailing={backgroundQuickControls}
      />}

      {!hideDaylightControls && onDaylightChange && (
        <div className={`mt-2 flex rounded-xl p-1 ${isDaylight ? 'bg-black/5' : 'bg-black/20'}`}>
          {([
            { value: true, label: t('ui.themeLight'), swatch: '#f5f5f4' },
            { value: false, label: t('ui.themeDark'), swatch: '#09090b' },
          ] as const).map(option => {
            const isActive = isDaylight === option.value;
            return (
              <button
                key={option.label}
                type="button"
                onClick={() => onDaylightChange(option.value)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-1.5 text-[11px] font-medium tracking-[0.04em] transition-all ${
                  isActive
                    ? isDaylight
                      ? 'bg-white shadow-sm'
                      : 'bg-white/20 shadow-sm'
                    : 'opacity-40 hover:opacity-100'
                }`}
                aria-pressed={isActive}
                aria-label={`${t('ui.themeMode')}: ${option.label}`}
              >
                <span
                  className="h-3 w-3 rounded-full border border-black/10"
                  style={{ backgroundColor: option.swatch }}
                />
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      {/* 先去掉「更多设置」详细参数面板
      {detailSettingsOpen && (
        <div className="mt-3 max-h-[40vh] overflow-y-auto rounded-xl border border-white/10 p-3">
          <LyricStageSettingsPanel
            t={t}
            theme={theme}
            isDaylight={isDaylight}
            visualizerMode={visualizerMode}
            onVisualizerModeChange={onVisualizerModeChange}
            backgroundConfig={backgroundConfig}
            backgroundActions={backgroundActions}
            {...settingsPanelExtra}
          />
        </div>
      )}
      */}
    </div>
  );
};

export default LyricStageAppearanceControls;
